import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import Person from '@mui/icons-material/Person';
import OfferPartnerFormSummary from './OfferPartnerFormSummary';
import OfferPartnerForm from './OfferPartnerForm';
import { CollapseCard, Dialog, Button } from '@totalsoft/rocket-ui';
import WrappedFakeText from 'components/common/WrappedFakeText';
import { BoxActionsWrapper } from 'components/common/CommonStyles';

const OfferPartnerFormCard = ({
  partnerId,
  partner,
  isLoaded,
  loading,
  isAssociatedPartner,
  isRealBeneficiary,
  realBeneficiaryRole,
  onPropertyChange,
  onUniqueIdBlur,
  onSave,
  onGDPRPrint,
  validationInfo,
  checkMandatoryFields,
  isDirty,
  saving,
  saveTriggered,
  deleting,
  expanded,
  printingGdpr,
  onCardToggle,
  t,
  documentId,
  siteId,
  onDelete,
  onDeleteDocumentPartner,
  addressTypes,
  onAddAddress,
  onDeleteAddress,
  defaultPartnerType,
  dictionaryAddressType,
  offerTypeId,
  isFacilityFrame,
  canSearchExternally,
  handleExternalSearch,
  setIsPartnerFromModal,
  waitingForOCR,
  partnerAddresses,
  socialAddressTypeId,
  loadingByUniqueId
}) => {
  const [openRemovePartnerConfirm, setOpenRemovePartnerConfirm] = useState(false);

  const handleDelete = () => {
    setOpenRemovePartnerConfirm(false);
    onDelete();
  };

  const handleOpenRemoveConfirmation = () => {
    setOpenRemovePartnerConfirm(true);
  };

  const handleCloseRemoveConfirmation = () => {
    setOpenRemovePartnerConfirm(false);
  };

  return (
    <CollapseCard
      id={'offerPartnerFormCard'}
      icon={Person}
      title={isAssociatedPartner ? t('OfferEdit.Client.AssociatedPartner') : t('OfferEdit.Client.Title')}
      expanded={expanded}
      onToggle={onCardToggle}
      subheader={<OfferPartnerFormSummary partner={partner || {}} />}
      hideSubheaderOnExpand
      toggleOnHeaderClick
    >
      {partnerId && (!isLoaded || loading || loadingByUniqueId) && !saving && !window.location.pathname.includes('partner-files/new') ? (
        <WrappedFakeText />
      ) : (
        <>
          <Dialog
            open={openRemovePartnerConfirm}
            onClose={handleCloseRemoveConfirmation}
            aria-labelledby='alert-dialog-title'
            aria-describedby='alert-dialog-description'
            title={t('OfferEdit.Dialog.ConfirmRemovePartnerTitle')}
            content={t('OfferEdit.Dialog.ConfirmRemovePartnerDescription')}
            actions={
              <BoxActionsWrapper>
                <Button id={'offerPartnerFormCard_deleteAcceptBtn'} color='primary' autoFocus onClick={handleDelete}>
                  {t('OfferEdit.Client.Dialog.AcceptButton')}
                </Button>
                <Button id={'offerPartnerFormCard_deleteRejectBtn'} color='primary' onClick={handleCloseRemoveConfirmation}>
                  {t('OfferEdit.Client.Dialog.RejectButton')}
                </Button>
              </BoxActionsWrapper>
            }
          />
          <OfferPartnerForm
            partnerId={partnerId}
            partner={partner}
            documentId={documentId}
            siteId={siteId}
            isAssociatedPartner={isAssociatedPartner}
            isRealBeneficiary={isRealBeneficiary}
            realBeneficiaryRole={realBeneficiaryRole}
            validationInfo={validationInfo}
            checkMandatoryFields={checkMandatoryFields}
            isDirty={isDirty}
            saving={saving}
            saveTriggered={saveTriggered}
            deleting={deleting}
            printingGdpr={printingGdpr}
            onPropertyChange={onPropertyChange}
            onUniqueIdBlur={onUniqueIdBlur}
            onSave={onSave}
            socialAddressTypeId={socialAddressTypeId}
            onDelete={handleOpenRemoveConfirmation}
            onDeleteDocumentPartner={onDeleteDocumentPartner}
            onGDPRPrint={onGDPRPrint}
            addressTypes={addressTypes}
            onAddAddress={onAddAddress}
            setIsPartnerFromModal={setIsPartnerFromModal}
            onDeleteAddress={onDeleteAddress}
            defaultPartnerType={defaultPartnerType}
            dictionaryAddressType={dictionaryAddressType}
            offerTypeId={offerTypeId}
            partnerAddresses={partnerAddresses}
            isFacilityFrame={isFacilityFrame}
            canSearchExternally={canSearchExternally}
            handleExternalSearch={handleExternalSearch}
            waitingForOCR={waitingForOCR}
            loading={loading}
          />
        </>
      )}
    </CollapseCard>
  );
};

OfferPartnerFormCard.propTypes = {
  t: PropTypes.func.isRequired,
  partnerId: PropTypes.number.isRequired,
  partner: PropTypes.object.isRequired,
  documentId: PropTypes.number,
  siteId: PropTypes.number,
  isLoaded: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  isAssociatedPartner: PropTypes.bool,
  isRealBeneficiary: PropTypes.bool,
  realBeneficiaryRole: PropTypes.object,
  onPropertyChange: PropTypes.func.isRequired,
  onUniqueIdBlur: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  isDirty: PropTypes.bool.isRequired,
  validationInfo: PropTypes.object.isRequired,
  checkMandatoryFields: PropTypes.bool.isRequired,
  onCardToggle: PropTypes.func.isRequired,
  expanded: PropTypes.bool.isRequired,
  saving: PropTypes.bool.isRequired,
  deleting: PropTypes.bool.isRequired,
  onDelete: PropTypes.func.isRequired,
  onDeleteDocumentPartner: PropTypes.func.isRequired,
  onGDPRPrint: PropTypes.func.isRequired,
  printingGdpr: PropTypes.bool.isRequired,
  addressTypes: PropTypes.string,
  onAddAddress: PropTypes.func.isRequired,
  onDeleteAddress: PropTypes.func.isRequired,
  defaultPartnerType: PropTypes.object,
  dictionaryAddressType: PropTypes.array.isRequired,
  saveTriggered: PropTypes.bool,
  offerTypeId: PropTypes.number,
  isFacilityFrame: PropTypes.bool,
  canSearchExternally: PropTypes.object.isRequired,
  handleExternalSearch: PropTypes.func.isRequired,
  setIsPartnerFromModal: PropTypes.func.isRequired,
  waitingForOCR: PropTypes.bool.isRequired,
  socialAddressTypeId: PropTypes.number,
  partnerAddresses: PropTypes.array,
  loadingByUniqueId: PropTypes.bool.isRequired
};

export default withTranslation()(React.memo(OfferPartnerFormCard));
